(function() {
  /** @type {WeakMap<HTMLElement, any>} */
  const initializedCarousels = new WeakMap();
  /** @type {WeakMap<HTMLElement, any>} */
  const initializedTabs = new WeakMap();
  /** @type {WeakMap<HTMLElement, any>} */
  const initializedTabsCarousels = new WeakMap();

  /**
   * @param {HTMLElement} container
   */
  function initTabsCarousel(container) {
    if (!container) return;

    const tabsCarouselWrapper = container.querySelector('.nf-featured-collection__tabs-carousel');
    if (!tabsCarouselWrapper || !(tabsCarouselWrapper instanceof HTMLElement)) return;

    const tabsCarouselElement = tabsCarouselWrapper.querySelector('.nf-featured-collection__tabs');
    if (!tabsCarouselElement || !(tabsCarouselElement instanceof HTMLElement)) return;

    const getSlidesToScroll = () => {
      return window.matchMedia('(min-width: 768px)').matches ? 4 : 2;
    };

    const slides = Array.from(tabsCarouselElement.querySelectorAll('.nf-featured-collection__tabs-slide'));
    if (slides.length === 0) return;

    // Get button references
    const tabsWrapper = container.querySelector('.nf-featured-collection__tabs-wrapper');
    if (!tabsWrapper || !(tabsWrapper instanceof HTMLElement)) return;
    
    const prevButton = tabsWrapper.querySelector('.nf-featured-collection__tabs-button--prev');
    const nextButton = tabsWrapper.querySelector('.nf-featured-collection__tabs-button--next');
    const prevButtonMobile = tabsWrapper.querySelector('.nf-featured-collection__tabs-button--prev-mobile');
    const nextButtonMobile = tabsWrapper.querySelector('.nf-featured-collection__tabs-button--next-mobile');

    // Skip if already initialized
    if (initializedTabsCarousels.has(tabsCarouselElement)) {
      // Still update button states even if already initialized
      const canPrev = tabsCarouselElement.scrollLeft > 0;
      const maxScroll = tabsCarouselElement.scrollWidth - tabsCarouselElement.clientWidth;
      const canNext = tabsCarouselElement.scrollLeft < maxScroll - 1;
      
      if (prevButton && prevButton instanceof HTMLElement && 'disabled' in prevButton) {
        (/** @type {HTMLButtonElement} */ (prevButton)).disabled = !canPrev;
      }
      if (nextButton && nextButton instanceof HTMLElement && 'disabled' in nextButton) {
        (/** @type {HTMLButtonElement} */ (nextButton)).disabled = !canNext;
      }
      if (prevButtonMobile && prevButtonMobile instanceof HTMLElement && 'disabled' in prevButtonMobile) {
        (/** @type {HTMLButtonElement} */ (prevButtonMobile)).disabled = !canPrev;
      }
      if (nextButtonMobile && nextButtonMobile instanceof HTMLElement && 'disabled' in nextButtonMobile) {
        (/** @type {HTMLButtonElement} */ (nextButtonMobile)).disabled = !canNext;
      }
      return;
    }

    // Calculate scroll distance per click (width of one slide including gap)
    const getScrollDistance = () => {
      const currentSlides = Array.from(tabsCarouselElement.querySelectorAll('.nf-featured-collection__tabs-slide'));
      if (currentSlides.length === 0) return 0;
      const firstSlide = currentSlides[0];
      if (!(firstSlide instanceof HTMLElement)) return 0;
      const slideWidth = firstSlide.offsetWidth;
      const gap = parseFloat(getComputedStyle(tabsCarouselElement).gap) || 8;
      const currentSlidesToScroll = getSlidesToScroll();
      return (slideWidth + gap) * currentSlidesToScroll;
    };

    const scrollToTab = (/** @type {number} */ targetIndex, /** @type {boolean} */ instant = false) => {
      const currentSlides = Array.from(tabsCarouselElement.querySelectorAll('.nf-featured-collection__tabs-slide'));
      if (currentSlides.length === 0 || targetIndex < 0 || targetIndex >= currentSlides.length) return;
      const targetSlide = currentSlides[targetIndex];
      if (!(targetSlide instanceof HTMLElement)) return;
      
      // Use offsetLeft which gives position relative to offsetParent
      // Account for container padding if any
      const containerPadding = parseFloat(getComputedStyle(tabsCarouselElement).paddingLeft) || 0;
      const scrollPosition = targetSlide.offsetLeft - containerPadding;
      
      tabsCarouselElement.scrollTo({
        left: scrollPosition,
        behavior: instant ? 'auto' : 'smooth'
      });
    };

    const scrollToActiveTab = (/** @type {boolean} */ instant = false) => {
      // Get fresh references
      const currentSlides = Array.from(tabsCarouselElement.querySelectorAll('.nf-featured-collection__tabs-slide'));
      if (currentSlides.length === 0) return;
      
      const activeTab = container.querySelector('.nf-featured-collection__tab--active');
      if (!activeTab) return;
      
      const activeSlide = activeTab.closest('.nf-featured-collection__tabs-slide');
      if (!activeSlide) return;
      
      const activeIndex = currentSlides.indexOf(activeSlide);
      if (activeIndex < 0) return;

      // Calculate which group contains this tab
      const currentSlidesToScroll = getSlidesToScroll();
      const groupIndex = Math.floor(activeIndex / currentSlidesToScroll);
      const targetIndex = groupIndex * currentSlidesToScroll;
      
      // On initial load (instant) and first tab is active, don't scroll to avoid misalignment
      // The natural scroll position 0 already shows the first tabs correctly
      if (instant && targetIndex === 0) {
        return;
      }
      
      // Otherwise, scroll to the target position
      if (instant) {
        scrollToTab(targetIndex, true);
      } else {
        if (targetIndex < currentSlides.length && targetIndex >= 0) {
          const targetSlide = currentSlides[targetIndex];
          if (targetSlide instanceof HTMLElement) {
            const containerPadding = parseFloat(getComputedStyle(tabsCarouselElement).paddingLeft) || 0;
            const targetScrollPosition = targetSlide.offsetLeft - containerPadding;
            const currentScrollPosition = tabsCarouselElement.scrollLeft;
            // Only scroll if we're not already at the correct position (allow 1px tolerance)
            if (Math.abs(currentScrollPosition - targetScrollPosition) > 1) {
              scrollToTab(targetIndex);
            }
          }
        } else {
          scrollToTab(targetIndex);
        }
      }
    };

    const canScrollPrev = () => {
      return tabsCarouselElement.scrollLeft > 0;
    };

    const canScrollNext = () => {
      const maxScroll = tabsCarouselElement.scrollWidth - tabsCarouselElement.clientWidth;
      return tabsCarouselElement.scrollLeft < maxScroll - 1;
    };

    const updateButtons = () => {
      const canPrev = canScrollPrev();
      const canNext = canScrollNext();
      
      if (prevButton && prevButton instanceof HTMLElement && 'disabled' in prevButton) {
        (/** @type {HTMLButtonElement} */ (prevButton)).disabled = !canPrev;
      }
      if (nextButton && nextButton instanceof HTMLElement && 'disabled' in nextButton) {
        (/** @type {HTMLButtonElement} */ (nextButton)).disabled = !canNext;
      }
      if (prevButtonMobile && prevButtonMobile instanceof HTMLElement && 'disabled' in prevButtonMobile) {
        (/** @type {HTMLButtonElement} */ (prevButtonMobile)).disabled = !canPrev;
      }
      if (nextButtonMobile && nextButtonMobile instanceof HTMLElement && 'disabled' in nextButtonMobile) {
        (/** @type {HTMLButtonElement} */ (nextButtonMobile)).disabled = !canNext;
      }
    };

    const scrollPrev = () => {
      const scrollDistance = getScrollDistance();
      const currentScroll = tabsCarouselElement.scrollLeft;
      const newScroll = Math.max(0, currentScroll - scrollDistance);
      
      tabsCarouselElement.scrollTo({
        left: newScroll,
        behavior: 'smooth'
      });
    };

    const scrollNext = () => {
      const scrollDistance = getScrollDistance();
      const currentScroll = tabsCarouselElement.scrollLeft;
      const maxScroll = tabsCarouselElement.scrollWidth - tabsCarouselElement.clientWidth;
      const newScroll = Math.min(maxScroll, currentScroll + scrollDistance);
      
      tabsCarouselElement.scrollTo({
        left: newScroll,
        behavior: 'smooth'
      });
    };

    // Attach button listeners
    if (prevButton && !prevButton.hasAttribute('data-listener-attached')) {
      prevButton.setAttribute('data-listener-attached', 'true');
      prevButton.addEventListener('click', (e) => {
        e.preventDefault();
        scrollPrev();
      });
    }

    if (nextButton && !nextButton.hasAttribute('data-listener-attached')) {
      nextButton.setAttribute('data-listener-attached', 'true');
      nextButton.addEventListener('click', (e) => {
        e.preventDefault();
        scrollNext();
      });
    }

    if (prevButtonMobile && !prevButtonMobile.hasAttribute('data-listener-attached')) {
      prevButtonMobile.setAttribute('data-listener-attached', 'true');
      prevButtonMobile.addEventListener('click', (e) => {
        e.preventDefault();
        scrollPrev();
      });
    }

    if (nextButtonMobile && !nextButtonMobile.hasAttribute('data-listener-attached')) {
      nextButtonMobile.setAttribute('data-listener-attached', 'true');
      nextButtonMobile.addEventListener('click', (e) => {
        e.preventDefault();
        scrollNext();
      });
    }

    // Update buttons on scroll
    const handleScroll = () => {
      updateButtons();
    };

    tabsCarouselElement.addEventListener('scroll', handleScroll);

    // Store reference for later use
    initializedTabsCarousels.set(tabsCarouselElement, {
      scrollToTab,
      scrollToActiveTab,
      slidesToScroll: getSlidesToScroll()
    });

    // Initial setup
    requestAnimationFrame(() => {
      setTimeout(() => {
        updateButtons();
        scrollToActiveTab(true); // Use instant scrolling on initial load
      }, 50);
    });
  }

  /**
   * @param {HTMLElement} container
   */
  function initFeaturedCollection(container) {
    if (!container) return;
    
    // Skip if already initialized
    if (initializedTabs.has(container)) return;

    const tabButtons = container.querySelectorAll('.nf-featured-collection__tab');
    const tabPanels = container.querySelectorAll('.nf-featured-collection__panel');
    const carouselElements = container.querySelectorAll('.nf-featured-collection__carousel');

    if (!tabButtons.length || !tabPanels.length) {
      console.warn('Featured Collection: No tabs or panels found');
      return;
    }

    // Find the first visible panel
    let activeTabIndex = 0;
    tabPanels.forEach((/** @type {Element} */ panel, /** @type {number} */ index) => {
      if (!panel.hasAttribute('hidden') && !panel.classList.contains('hidden')) {
        activeTabIndex = index;
      }
    });

    // Initialize first carousel - find the first visible/active panel's carousel
    const firstActivePanel = Array.from(tabPanels).find(panel => 
      !panel.hasAttribute('hidden') && !panel.classList.contains('hidden')
    );
    if (firstActivePanel && firstActivePanel instanceof HTMLElement) {
      const firstCarousel = firstActivePanel.querySelector('.nf-featured-collection__carousel');
      if (firstCarousel && firstCarousel instanceof HTMLElement) {
        initCarousel(firstCarousel, container);
      }
    } else if (carouselElements[0] && carouselElements[0] instanceof HTMLElement) {
      // Fallback to first carousel if no active panel found
      initCarousel(carouselElements[0], container);
    }

    /**
     * @param {number} index
     */
    function switchTab(index) {
      if (index === activeTabIndex) return;
      
      // Update tab buttons
      tabButtons.forEach((/** @type {Element} */ button, /** @type {number} */ i) => {
        const isActive = i === index;
        button.classList.toggle('nf-featured-collection__tab--active', isActive);
        button.setAttribute('aria-selected', String(isActive));
        button.setAttribute('tabindex', isActive ? '0' : '-1');
        if (isActive) {
          button.classList.add('bg-[var(--color-primary-button-background)]', 'text-[var(--color-primary-button-text)]');
          button.classList.remove('bg-transparent', 'text-gray-800');
        } else {
          button.classList.remove('bg-[var(--color-primary-button-background)]', 'text-[var(--color-primary-button-text)]');
          button.classList.add('bg-transparent', 'text-gray-800');
        }
      });

      // Update tab panels
      tabPanels.forEach((/** @type {Element} */ panel, /** @type {number} */ i) => {
        const isActive = i === index;
        if (isActive) {
          panel.classList.add('nf-featured-collection__panel--active');
          panel.classList.remove('hidden');
          panel.removeAttribute('hidden');
          // Initialize or reinitialize carousel for newly active panel after a brief delay to ensure it's visible
          setTimeout(() => {
            const carousel = panel.querySelector('.nf-featured-collection__carousel');
            if (carousel && carousel instanceof HTMLElement) {
              // If carousel was already initialized, reset position and recalculate dots
              if (initializedCarousels.has(carousel)) {
                const carouselData = initializedCarousels.get(carousel);
                if (carouselData?.embla) {
                  // ReInit to ensure everything is recalculated
                  // The reInit handler will handle scrolling to 0 and updating dots
                  carouselData.embla.reInit();
                }
              } else {
                initCarousel(carousel, container);
              }
            }
          }, 50);
        } else {
          panel.classList.remove('nf-featured-collection__panel--active');
          panel.classList.add('hidden');
          panel.setAttribute('hidden', '');
        }
      });

      activeTabIndex = index;

      // Scroll active tab into view in tabs carousel
      setTimeout(() => {
        const tabsCarouselWrapper = container.querySelector('.nf-featured-collection__tabs-carousel');
        if (tabsCarouselWrapper && tabsCarouselWrapper instanceof HTMLElement) {
          const tabsCarousel = tabsCarouselWrapper.querySelector('.nf-featured-collection__tabs');
          if (tabsCarousel && tabsCarousel instanceof HTMLElement) {
            const tabsData = initializedTabsCarousels.get(tabsCarousel);
            if (tabsData?.scrollToActiveTab) {
              tabsData.scrollToActiveTab();
            }
          }
        }
      }, 50);
    }

    // Add event listeners to tab buttons
    tabButtons.forEach((/** @type {Element} */ button, /** @type {number} */ index) => {
      const buttonElement = button instanceof HTMLElement ? button : null;
      const tabIndex = buttonElement?.dataset?.tabIndex ? parseInt(buttonElement.dataset.tabIndex, 10) : index;
      
      button.addEventListener('click', (/** @type {Event} */ e) => {
        e.preventDefault();
        switchTab(index);
      });
      
      button.addEventListener('keydown', (/** @type {KeyboardEvent} */ e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          switchTab(index);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          const nextIndex = (index + 1) % tabButtons.length;
          switchTab(nextIndex);
          const nextButton = tabButtons[nextIndex];
          if (nextButton && 'focus' in nextButton) {
            (/** @type {HTMLElement} */ (nextButton)).focus();
          }
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          const prevIndex = (index - 1 + tabButtons.length) % tabButtons.length;
          switchTab(prevIndex);
          const prevButton = tabButtons[prevIndex];
          if (prevButton && 'focus' in prevButton) {
            (/** @type {HTMLElement} */ (prevButton)).focus();
          }
        }
      });
    });

    // Initialize tabs carousel
    initTabsCarousel(container);

    // Handle window resize to update tabs carousel when slidesToScroll changes
    if (!container.hasAttribute('data-tabs-resize-handler')) {
      container.setAttribute('data-tabs-resize-handler', 'true');
      /** @type {ReturnType<typeof setTimeout> | undefined} */
      let resizeTimer;
      const handleResize = () => {
        if (resizeTimer !== undefined) {
          clearTimeout(resizeTimer);
        }
        resizeTimer = setTimeout(() => {
          const tabsCarouselWrapper = container.querySelector('.nf-featured-collection__tabs-carousel');
          if (tabsCarouselWrapper && tabsCarouselWrapper instanceof HTMLElement) {
            const tabsCarouselElement = tabsCarouselWrapper.querySelector('.nf-featured-collection__tabs');
            if (tabsCarouselElement && tabsCarouselElement instanceof HTMLElement) {
              const getSlidesToScroll = () => {
                return window.matchMedia('(min-width: 768px)').matches ? 4 : 2;
              };
              const newSlidesToScroll = getSlidesToScroll();
              const tabsData = initializedTabsCarousels.get(tabsCarouselElement);
              
              // Update slidesToScroll if changed, but keep the instance
              if (tabsData && tabsData.slidesToScroll !== newSlidesToScroll) {
                tabsData.slidesToScroll = newSlidesToScroll;
                // Update buttons after resize
                const tabsWrapper = container.querySelector('.nf-featured-collection__tabs-wrapper');
                if (tabsWrapper) {
                  const prevButton = tabsWrapper.querySelector('.nf-featured-collection__tabs-button--prev');
                  const nextButton = tabsWrapper.querySelector('.nf-featured-collection__tabs-button--next');
                  const prevButtonMobile = tabsWrapper.querySelector('.nf-featured-collection__tabs-button--prev-mobile');
                  const nextButtonMobile = tabsWrapper.querySelector('.nf-featured-collection__tabs-button--next-mobile');
                  
                  const canPrev = tabsCarouselElement.scrollLeft > 0;
                  const maxScroll = tabsCarouselElement.scrollWidth - tabsCarouselElement.clientWidth;
                  const canNext = tabsCarouselElement.scrollLeft < maxScroll - 1;
                  
                  if (prevButton && prevButton instanceof HTMLElement && 'disabled' in prevButton) {
                    (/** @type {HTMLButtonElement} */ (prevButton)).disabled = !canPrev;
                  }
                  if (nextButton && nextButton instanceof HTMLElement && 'disabled' in nextButton) {
                    (/** @type {HTMLButtonElement} */ (nextButton)).disabled = !canNext;
                  }
                  if (prevButtonMobile && prevButtonMobile instanceof HTMLElement && 'disabled' in prevButtonMobile) {
                    (/** @type {HTMLButtonElement} */ (prevButtonMobile)).disabled = !canPrev;
                  }
                  if (nextButtonMobile && nextButtonMobile instanceof HTMLElement && 'disabled' in nextButtonMobile) {
                    (/** @type {HTMLButtonElement} */ (nextButtonMobile)).disabled = !canNext;
                  }
                }
              }
            }
          }
        }, 250);
      };
      window.addEventListener('resize', handleResize);
    }

    initializedTabs.set(container, { switchTab, activeTabIndex });
  }

  /**
   * @param {HTMLElement} carouselElement
   * @param {HTMLElement} container
   */
  function initCarousel(carouselElement, container) {
    if (!carouselElement) return;
    
    // Skip if already initialized
    if (initializedCarousels.has(carouselElement)) return;

    if (typeof EmblaCarousel === 'undefined') {
      setTimeout(() => initCarousel(carouselElement, container), 100);
      return;
    }

    const getSlidesToScroll = () => {
      return window.matchMedia('(min-width: 768px)').matches ? 3 : 1;
    };

    const getAlign = () => {
      return window.matchMedia('(min-width: 768px)').matches ? 'start' : 'center';
    };

    const currentSlidesToScroll = getSlidesToScroll();
    const currentAlign = getAlign();
    const embla = EmblaCarousel(carouselElement, {
      loop: false,
      align: currentAlign,
      containScroll: 'trimSnaps',
      slidesToScroll: currentSlidesToScroll,
      dragFree: false
    });

    initializedCarousels.set(carouselElement, { embla, slidesToScroll: currentSlidesToScroll, align: currentAlign });

    const carouselWrapper = carouselElement.closest('.nf-featured-collection__carousel-wrapper');
    const prevButton = carouselWrapper?.querySelector('.nf-featured-collection__button--prev');
    const nextButton = carouselWrapper?.querySelector('.nf-featured-collection__button--next');
    const dotsContainer = carouselWrapper?.querySelector('.nf-featured-collection__dots');
    const slides = carouselElement.querySelectorAll('.nf-featured-collection__slide');

    const setupDots = () => {
      if (!dotsContainer) return;
      
      try {
        // Ensure we're working with the correct carousel's slides
        const currentSlides = carouselElement.querySelectorAll('.nf-featured-collection__slide');
        
        if (!currentSlides.length) {
          dotsContainer.innerHTML = '';
          return;
        }
        
        // Get the actual snap points from Embla
        // This is more reliable than calculating manually, especially with slidesToScroll
        let totalPages = 0;
        try {
          // @ts-ignore - scrollSnapList may not be in type definitions but exists at runtime
          const snapList = embla.scrollSnapList();
          totalPages = snapList?.length || 0;
        } catch (e) {
          // Fallback: calculate manually if scrollSnapList isn't available
          const carouselData = initializedCarousels.get(carouselElement);
          const visibleSlidesPerPage = carouselData ? carouselData.slidesToScroll : currentSlidesToScroll;
          totalPages = Math.max(1, Math.ceil(currentSlides.length / visibleSlidesPerPage));
        }
        
        // Only show dots if there are multiple pages
        if (totalPages <= 1) {
          dotsContainer.innerHTML = '';
          return;
        }
        
        // Clear existing dots
        dotsContainer.innerHTML = '';
        
        // Create dots based on actual snap points for THIS specific carousel
        // Each dot corresponds directly to a snap index (0, 1, 2, ...)
        for (let i = 0; i < totalPages; i++) {
          const dot = document.createElement('button');
          dot.className = 'nf-featured-collection__dot w-3 h-3 rounded-full border-2 border-[var(--color-primary-button-background,#000)] bg-[var(--color-primary-button-background,#000)] opacity-25 cursor-pointer p-0 transition-all duration-200 ease-in-out';
          dot.setAttribute('type', 'button');
          dot.setAttribute('aria-label', `Go to page ${i + 1}`);
          
          // Each dot corresponds directly to a snap index
          const snapIndex = i;
          dot.addEventListener('click', () => {
            embla.scrollTo(snapIndex);
          });
          
          dotsContainer.appendChild(dot);
        }
      } catch (error) {
        console.warn('Featured Collection: Error setting up dots', error);
        dotsContainer.innerHTML = '';
      }
    };

    const updateDots = () => {
      if (!dotsContainer) return;
      
      const dots = dotsContainer.querySelectorAll('.nf-featured-collection__dot');
      if (dots.length === 0) return;
      
      const carouselData = initializedCarousels.get(carouselElement);
      if (!carouselData) return;
      
      try {
        // Get the current snap index (this is the page index when using slidesToScroll)
        const selectedSnapIndex = embla.selectedScrollSnap();
        
        // Ensure activePageIndex is within valid range
        const activePageIndex = Math.max(0, Math.min(selectedSnapIndex, dots.length - 1));
        
        dots.forEach((/** @type {Element} */ dot, /** @type {number} */ index) => {
          if (index === activePageIndex) {
            dot.classList.add('nf-featured-collection__dot--active', '!opacity-100', '!w-[45px]', '!rounded-[18px]');
            dot.classList.remove('opacity-25');
          } else {
            dot.classList.remove('nf-featured-collection__dot--active', '!opacity-100', '!w-[45px]', '!rounded-[18px]');
            dot.classList.add('opacity-25');
          }
        });
      } catch (error) {
        console.warn('Featured Collection: Error updating dots', error);
      }
    };

    const updateButtons = () => {
      if (prevButton && prevButton instanceof HTMLElement && 'disabled' in prevButton) {
        (/** @type {HTMLButtonElement} */ (prevButton)).disabled = !embla.canScrollPrev();
      }
      if (nextButton && nextButton instanceof HTMLElement && 'disabled' in nextButton) {
        (/** @type {HTMLButtonElement} */ (nextButton)).disabled = !embla.canScrollNext();
      }
    };

    if (prevButton) {
      prevButton.addEventListener('click', () => embla.scrollPrev());
    }

    if (nextButton) {
      nextButton.addEventListener('click', () => embla.scrollNext());
    }

    embla.on('select', () => {
      updateButtons();
      updateDots();
    });

    embla.on('init', () => {
      // Wait for Embla to fully initialize before setting up dots
      // Need to wait for scrollSnapList to be available
      requestAnimationFrame(() => {
        setTimeout(() => {
          setupDots();
          updateButtons();
          // Ensure we're at the first position and update active dot
          embla.scrollTo(0);
          updateDots();
        }, 50);
      });
    });

    embla.on('reInit', () => {
      // Recalculate dots when carousel is reinitialized (e.g., on tab switch)
      requestAnimationFrame(() => {
        setTimeout(() => {
          setupDots();
          updateButtons();
          // After dots are set up, ensure we're at the first position and update active dot
          embla.scrollTo(0);
          updateDots();
        }, 50);
      });
    });
    
    // Recalculate dots when the panel becomes visible
    const panel = carouselElement.closest('.nf-featured-collection__panel');
    if (panel) {
      const observer = new MutationObserver(() => {
        if (!panel.hasAttribute('hidden') && !panel.classList.contains('hidden')) {
          setTimeout(() => {
            setupDots();
            updateDots();
          }, 100);
        }
      });
      
      observer.observe(panel, {
        attributes: true,
        attributeFilter: ['hidden', 'class']
      });
    }

    // @ts-ignore - settle event may not be in type definitions but exists at runtime
    embla.on('settle', () => {
      updateDots();
    });

    // Initial setup - create dots after carousel data is stored
    // setupDots will be called in the 'init' event
    updateButtons();

    // Handle window resize to adjust slidesToScroll and recalculate dots
    /** @type {ReturnType<typeof setTimeout> | undefined} */
    let resizeTimer;
    const handleResize = () => {
      if (resizeTimer !== undefined) {
        clearTimeout(resizeTimer);
      }
      resizeTimer = setTimeout(() => {
        const carouselData = initializedCarousels.get(carouselElement);
        if (!carouselData) return;
        
        const newSlidesToScroll = getSlidesToScroll();
        const newAlign = getAlign();
        if (carouselData.slidesToScroll !== newSlidesToScroll || carouselData.align !== newAlign) {
          carouselData.embla.destroy();
          initializedCarousels.delete(carouselElement);
          initCarousel(carouselElement, container);
        } else {
          // If slidesToScroll didn't change, just recalculate dots in case slides changed
          setupDots();
          updateDots();
        }
      }, 250);
    };
    window.addEventListener('resize', handleResize);
  }

  function initAllFeaturedCollections() {
    const containers = document.querySelectorAll('.nf-featured-collection:not([data-initialized])');
    containers.forEach((container) => {
      if (container instanceof HTMLElement) {
        container.setAttribute('data-initialized', 'true');
        initFeaturedCollection(container);
      }
    });
  }

  function handleThemeEditorUpdates() {
    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:load', initAllFeaturedCollections);
      document.addEventListener('shopify:section:reorder', initAllFeaturedCollections);
      document.addEventListener('shopify:block:select', initAllFeaturedCollections);
    }
  }

  // Initial load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initAllFeaturedCollections();
      handleThemeEditorUpdates();
    });
  } else {
    initAllFeaturedCollections();
    handleThemeEditorUpdates();
  }

  // Use MutationObserver for theme editor updates
  if (window.Shopify?.designMode) {
    const observer = new MutationObserver(() => {
      initAllFeaturedCollections();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
})();

